import { useFetch } from "@hyper-fetch/react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Mails } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";

import { ArchiveHeader } from "./components/archive-header";
import { NewsletterHeroSignup } from "../newsletter-hero-signup/newsletter-hero-signup";
import { sdk } from "@/api/sdk";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";
import type { PublicNewsletterRelease } from "@portfolio/enums";

const PAGE_SIZE = 12;

const issueDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(getLocale(), { year: "numeric", month: "long", day: "numeric" });

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
};

/**
 * The hosted archive as a two-column spread: the signup card sits on the left
 * as a sticky rail, and every published issue lists on the right as a generous
 * card row. Rows carry the date as a quiet overline, the subject as the
 * headline, and lift on hover with an arrow leaning out; new pages append
 * below via cursor pagination. On mobile the rail stacks above the list.
 */
export function NewsletterArchive() {
  const [pages, setPages] = useState<PublicNewsletterRelease[][]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { data, loading } = useFetch(
    sdk.newsletter.releases.$get.setQueryParams({ limit: PAGE_SIZE, ...(cursor ? { after: cursor } : {}) }),
  );

  // Cursor pagination that appends: the current fetch is the "live" page; older pages are pinned
  // into local state the moment the reader asks for more.
  const releases = [...pages.flat(), ...(data?.data ?? [])];
  const nextCursor = data?.nextCursor;

  const loadMore = () => {
    if (!nextCursor || !data) return;
    setPages((current) => [...current, data.data]);
    setCursor(nextCursor);
  };

  return (
    <div className="min-h-svh px-5 py-10 sm:px-8 md:py-14">
      <div className="mx-auto w-full max-w-[96rem]">
        <ArchiveHeader />

        <div className="grid gap-10 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] lg:gap-14">
          {/* Always the email-collecting rail: the archive is public, so it can never assume
              a session. The one-tap membership card ships unmounted for in-app surfaces. */}
          <motion.aside
            {...fadeUp}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:sticky lg:top-10 lg:self-start"
          >
            <NewsletterHeroSignup
              source="archive"
              title={m.newsletter_archive_title()}
              description={m.newsletter_archive_description()}
            />
          </motion.aside>

          <section>
            <div className="space-y-3" data-tour="newsletter-archive-list">
              {loading && releases.length === 0 ? (
                <>
                  <Skeleton className="h-24 rounded-2xl" />
                  <Skeleton className="h-24 rounded-2xl" />
                  <Skeleton className="h-24 rounded-2xl" />
                </>
              ) : releases.length === 0 ? (
                /* Empty archive: ghost issue rows fading down the column, with the promise of the
                   first issue floating over them. Reads as "this is where issues will live", not
                   as an error. */
                <div className="relative">
                  <div aria-hidden className="space-y-3">
                    {["opacity-65", "opacity-40", "opacity-20", "opacity-10"].map((opacity) => (
                      <div key={opacity} className={`bg-card/40 rounded-2xl border p-6 ${opacity}`}>
                        <div className="bg-muted h-2.5 w-28 rounded-full" />
                        <div className="bg-muted mt-3 h-4 w-2/3 rounded-full" />
                        <div className="bg-muted/70 mt-2.5 h-3 w-1/2 rounded-full" />
                      </div>
                    ))}
                  </div>
                  <div className="absolute inset-0 grid place-items-center">
                    <motion.div
                      {...fadeUp}
                      transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                      className="bg-background/70 flex max-w-sm flex-col items-center gap-2 rounded-3xl px-8 py-6 text-center backdrop-blur-sm"
                    >
                      <span className="bg-muted text-muted-foreground grid size-11 place-items-center rounded-2xl">
                        <Mails className="size-5" />
                      </span>
                      <h2 className="mt-1 text-lg font-semibold tracking-tight">
                        {m.newsletter_archive_empty_title()}
                      </h2>
                      <p className="text-muted-foreground text-sm leading-6">{m.newsletter_archive_empty()}</p>
                    </motion.div>
                  </div>
                </div>
              ) : (
                releases.map((release, index) => (
                  <motion.div
                    key={release.id}
                    {...fadeUp}
                    transition={{
                      duration: 0.35,
                      delay: Math.min((index % PAGE_SIZE) * 0.04, 0.3),
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <Link
                      to="/newsletter/$releaseId"
                      params={{ releaseId: release.id }}
                      className="group bg-card/40 hover:border-primary/30 block rounded-2xl border p-6 transition-all hover:-translate-y-0.5 hover:shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
                            {issueDate(release.sentAt)}
                          </p>
                          <h2 className="mt-1.5 truncate text-lg font-semibold tracking-tight">
                            {release.subject || release.title}
                          </h2>
                          {release.preheader ? (
                            <p className="text-muted-foreground mt-1 line-clamp-2 text-sm leading-6">
                              {release.preheader}
                            </p>
                          ) : null}
                        </div>
                        <span className="bg-muted text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground mt-1 grid size-8 shrink-0 place-items-center rounded-full transition-colors">
                          <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))
              )}
            </div>

            {nextCursor ? (
              <div className="mt-8 flex justify-center">
                <Button variant="outline" className="rounded-full" onClick={loadMore}>
                  {m.newsletter_archive_load_more()}
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
